/**
 * PreviewPanel — thin adapter for the store-connected rendering pipeline.
 *
 * Assembles DocContext + ExecutionContext from PaneContext + Zustand stores.
 * All rendering is delegated to BlockList → BlockDispatcher → unified blocks.
 *
 * Editor-specific features (pin, config sidebar, debug panel, ::use config
 * popover) are injected via context callbacks and ContentBlockWrapper.
 */

import { useT } from "@magic-md/i18n/react";
import type { DocumentBlock, ForBlock, ImportBinding, PluginOutput } from "@magic-md/core";
import type { RendererConfigChangeOptions } from "@magic-md/plugin-sdk";
import type { CSSProperties, MouseEvent, RefObject } from "react";
import {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from "react";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import rehypeKatex from "rehype-katex";
import remarkDefinitionList, { defListHastHandlers } from "remark-definition-list";
import remarkFrontmatter from "remark-frontmatter";
import remarkGfm from "remark-gfm";
import { remarkAlert } from "remark-github-blockquote-alert";
import remarkMath from "remark-math";
import { cn } from "../../lib/utils";
import { getFileKind } from "../../lib/file-type";
import { pickerInitialPathFromDocument } from "../../lib/path-utils";
import {
    createWorkspacePinnedUnit,
    findWorkspacePinnedUnit,
} from "../../lib/pinned-units";
import {
    PREVIEW_HORIZONTAL_PADDING,
    type PreviewWidthControl,
    clampPreviewViewportWidth,
    createCustomPreviewWidth,
    resolvePreviewViewportWidth,
    splitPreviewWidthValue,
} from "../../lib/preview-width";
import { getDocumentBundleDir } from "../../lib/publish-utils";
import { findParentContentBlockId } from "../../lib/scope-variables";
import {
    useStableDocumentBlocks,
    useStableSemanticValue,
} from "../../lib/use-stable-semantic-value";
import {
    resolveImportedUnitSource,
    updateDefinitionConfig,
    updateDefinitionConfigAndDropOutput,
} from "../../lib/unit-edit-target";
import { getPlatformService } from "../../platform";
import { pluginUIRegistry } from "../../plugins/plugin-ui-registry";
import { resolveRenderRunTarget, type RenderRunTarget } from "../../preview-logic";
import type { PreviewWidth } from "../../store/editor-store";
import { useEditorStore } from "../../store/editor-store";
import { useLogStore } from "../../store/log-store";
import { getNotificationBus } from "../../store/notification-bus";
import {
    usePluginExecutionStore,
    type ContentExecutionEntry,
    type ExecutionEntry,
    type ForExecutionEntry,
    type ImportExecutionEntry,
    type UseExecutionEntry,
} from "../../store/plugin-execution-store";
import { showFilePickerDialog, showFolderPickerDialog } from "../dialogs/DialogHost";
import { AnnotationProvider } from "../annotations/AnnotationProvider";
import { BlockList, type BlockListProps } from "../document-renderer/blocks/BlockList";
import {
    assetRefUrlTransform,
    createAssetRefRemarkPlugin,
    findMarkdownHeadingLine,
    type MarkdownLinkOpenTarget,
    withAssetRefMarkdownComponents,
} from "../document-renderer/asset-refs";
import type { DocContextValue, DocOutputsStore } from "../document-renderer/contexts/DocContext";
import { DocContextProvider, DocOutputsProvider } from "../document-renderer/contexts/DocContext";
import type { DefinitionExecutionState, ExecutionContextValue } from "../document-renderer/contexts/ExecutionContext";
import { ExecutionContextProvider } from "../document-renderer/contexts/ExecutionContext";
import { normalizeRenderedMarkdown } from "../document-renderer/utils";
import {
    PaneRenderProvider,
    type PaneRenderContextValue,
    usePaneRenderContext,
} from "../PaneContext";
import { ContentBlockWrapper } from "./ContentBlockWrapper";
import { LazyBlock } from "./LazyBlock";
import { CodeTextPreview } from "./CodeTextPreview";
import { CsvPreview } from "./CsvPreview";
import { HtmlIframePreview } from "./HtmlIframePreview";
import { ImagePreview } from "./ImagePreview";
import { JsonTreePreview } from "./JsonTreePreview";
import { MediaPreview } from "./MediaPreview";
import { PdfPreview } from "./PdfPreview";

function subscribePluginRegistry(onStoreChange: () => void) {
    return pluginUIRegistry.subscribe(onStoreChange);
}

let cachedPluginRegistrySnapshot = "";
function getPluginRegistrySnapshot() {
    const newSnapshot = `${pluginUIRegistry.revision()}:${pluginUIRegistry.names().join("\0")}`;
    if (newSnapshot !== cachedPluginRegistrySnapshot) {
        cachedPluginRegistrySnapshot = newSnapshot;
    }
    return cachedPluginRegistrySnapshot;
}

function readPreviewAvailableWidth(element: HTMLElement | null): number | null {
    if (!element) return null;
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0) return null;
    return Math.max(1, rect.width - PREVIEW_HORIZONTAL_PADDING);
}

const EMPTY_EXECUTIONS = new Map<string, ExecutionEntry>();
const EMPTY_FOR_EXECUTIONS = new Map<string, ForExecutionEntry>();
const EMPTY_CONTENT_EXECUTIONS = new Map<string, ContentExecutionEntry>();
const EMPTY_IMPORT_EXECUTIONS = new Map<string, ImportExecutionEntry>();
const EMPTY_USE_EXECUTIONS = new Map<string, UseExecutionEntry>();
const EMPTY_IMPORT_BINDINGS: readonly ImportBinding[] = [];
const EMPTY_DEFINITION_EXECUTION_STATE: DefinitionExecutionState = {
    isPinned: false,
    isRunning: false,
    errorMessage: null,
};

type PluginExecutionStoreSnapshot = ReturnType<typeof usePluginExecutionStore.getState>;
type ExecutionMapSelector<T> = (
    store: PluginExecutionStoreSnapshot,
    tabId: string,
    activeTabId: string | null | undefined,
) => Map<string, T>;

function getPaneSessionSnapshot(
    store: PluginExecutionStoreSnapshot,
    tabId: string,
    activeTabId: string | null | undefined,
) {
    if (tabId === activeTabId) return null;
    const sessionId = store.tabSessionMap.get(tabId);
    if (!sessionId) return null;
    return store.sessionExecutionCache.get(sessionId) ?? null;
}

function getPaneExecutions(
    store: PluginExecutionStoreSnapshot,
    tabId: string,
    activeTabId: string | null | undefined,
): Map<string, ExecutionEntry> {
    if (tabId === activeTabId) return store.executions;
    return getPaneSessionSnapshot(store, tabId, activeTabId)?.executions ?? EMPTY_EXECUTIONS;
}

function getPaneExecutionEntry(
    store: PluginExecutionStoreSnapshot,
    tabId: string,
    activeTabId: string | null | undefined,
    definitionId: string,
): ExecutionEntry | undefined {
    return getPaneExecutions(store, tabId, activeTabId).get(definitionId);
}

function getPaneForExecutions(
    store: PluginExecutionStoreSnapshot,
    tabId: string,
    activeTabId: string | null | undefined,
): Map<string, ForExecutionEntry> {
    if (tabId === activeTabId) return store.forExecutions;
    return getPaneSessionSnapshot(store, tabId, activeTabId)?.forExecutions ??
        EMPTY_FOR_EXECUTIONS;
}

function getPaneContentExecutions(
    store: PluginExecutionStoreSnapshot,
    tabId: string,
    activeTabId: string | null | undefined,
): Map<string, ContentExecutionEntry> {
    if (tabId === activeTabId) return store.contentExecutions;
    return getPaneSessionSnapshot(store, tabId, activeTabId)?.contentExecutions ??
        EMPTY_CONTENT_EXECUTIONS;
}

function getPaneImportExecutions(
    store: PluginExecutionStoreSnapshot,
    tabId: string,
    activeTabId: string | null | undefined,
): Map<string, ImportExecutionEntry> {
    if (tabId === activeTabId) return store.importExecutions;
    return getPaneSessionSnapshot(store, tabId, activeTabId)?.importExecutions ??
        EMPTY_IMPORT_EXECUTIONS;
}

function getPaneUseExecutions(
    store: PluginExecutionStoreSnapshot,
    tabId: string,
    activeTabId: string | null | undefined,
): Map<string, UseExecutionEntry> {
    if (tabId === activeTabId) return store.useExecutions;
    return getPaneSessionSnapshot(store, tabId, activeTabId)?.useExecutions ??
        EMPTY_USE_EXECUTIONS;
}

function getPaneExecutionMapEntry<T>(
    selector: ExecutionMapSelector<T>,
    tabId: string,
    id: string,
): T | undefined {
    const executionStore = usePluginExecutionStore.getState();
    const editorStore = useEditorStore.getState();
    return selector(executionStore, tabId, editorStore.activeTabId).get(id);
}

interface CachedDocOutputState {
    baseOutput: PluginOutput | undefined;
    partialOutput: Record<string, unknown> | undefined;
    value: PluginOutput | undefined;
}

export function prunePreviewDefinitionCaches(
    validDefinitionIds: ReadonlySet<string>,
    ...caches: Array<Pick<Map<string, unknown>, "keys" | "delete">>
): void {
    for (const cache of caches) {
        for (const definitionId of cache.keys()) {
            if (!validDefinitionIds.has(definitionId)) cache.delete(definitionId);
        }
    }
}

interface MarkdownPreviewDocumentProps {
    docValue: DocContextValue;
    outputStore: DocOutputsStore;
    execValue: ExecutionContextValue;
    previewContentRef: RefObject<HTMLDivElement | null>;
    previewContentStyle: CSSProperties;
    previewWidthControl: PreviewWidthControl;
    isPreviewWidthDragging: boolean;
    startPreviewWidthResize: (side: "left" | "right", event: MouseEvent<HTMLButtonElement>) => void;
    resetPreviewWidth: () => void;
    documentPath?: string;
    content: string;
    blocks: DocumentBlock[] | null;
    importDocuments: PaneRenderContextValue["importDocuments"];
    blockWrapper: BlockListProps["blockWrapper"];
    openFile: (filePath: string, target?: MarkdownLinkOpenTarget) => Promise<void>;
    openExternal: (url: string) => Promise<void>;
}

const MarkdownPreviewDocument = memo(function MarkdownPreviewDocument({
    docValue,
    outputStore,
    execValue,
    previewContentRef,
    previewContentStyle,
    previewWidthControl,
    isPreviewWidthDragging,
    startPreviewWidthResize,
    resetPreviewWidth,
    documentPath,
    content,
    blocks,
    importDocuments,
    blockWrapper,
    openFile,
    openExternal,
}: MarkdownPreviewDocumentProps) {
    const t = useT("editor");
    return (
        <DocContextProvider value={docValue}>
            <DocOutputsProvider value={outputStore}>
                <ExecutionContextProvider value={execValue}>
                    <div
                        ref={previewContentRef}
                        className="relative mx-auto min-h-full"
                        style={previewContentStyle}
                        data-preview-width={previewWidthControl}
                    >
                        <button
                            type="button"
                            role="separator"
                            aria-orientation="vertical"
                            aria-label={t("preview.resizeLeft")}
                            title={t("render.fullscreen.resizeTitle")}
                            className="group/resize absolute inset-y-0 left-0 z-20 w-2 -translate-x-3 cursor-col-resize border-0 bg-transparent p-0 outline-none"
                            onMouseDown={(event) => startPreviewWidthResize("left", event)}
                            onDoubleClick={resetPreviewWidth}
                        >
                            <span
                                aria-hidden="true"
                                className={cn(
                                    "absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors",
                                    isPreviewWidthDragging
                                        ? "bg-accent"
                                        : "bg-transparent group-hover/resize:bg-accent group-focus-visible/resize:bg-accent",
                                )}
                            />
                        </button>
                        <button
                            type="button"
                            role="separator"
                            aria-orientation="vertical"
                            aria-label={t("preview.resizeRight")}
                            title={t("render.fullscreen.resizeTitle")}
                            className="group/resize absolute inset-y-0 right-0 z-20 w-2 translate-x-3 cursor-col-resize border-0 bg-transparent p-0 outline-none"
                            onMouseDown={(event) => startPreviewWidthResize("right", event)}
                            onDoubleClick={resetPreviewWidth}
                        >
                            <span
                                aria-hidden="true"
                                className={cn(
                                    "absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors",
                                    isPreviewWidthDragging
                                        ? "bg-accent"
                                        : "bg-transparent group-hover/resize:bg-accent group-focus-visible/resize:bg-accent",
                                )}
                            />
                        </button>
                        <AnnotationProvider
                            documentPath={documentPath}
                            content={content}
                        >
                            <article className="preview-content prose prose-sm w-full max-w-none">
                                {blocks ? (
                                    <BlockList
                                        blocks={blocks}
                                        blockWrapper={blockWrapper}
                                    />
                                ) : (
                                    <ReactMarkdown
                                        remarkPlugins={[
                                            remarkGfm,
                                            [remarkFrontmatter, ["yaml", "toml"]],
                                            remarkDefinitionList,
                                            remarkMath,
                                            remarkAlert,
                                            createAssetRefRemarkPlugin(
                                                documentPath,
                                                importDocuments,
                                            ),
                                        ]}
                                        urlTransform={assetRefUrlTransform}
                                        remarkRehypeOptions={{ handlers: defListHastHandlers }}
                                        rehypePlugins={[rehypeKatex, rehypeHighlight]}
                                        components={withAssetRefMarkdownComponents(
                                            documentPath,
                                            {},
                                            { openFile, openExternal },
                                            importDocuments,
                                        )}
                                    >
                                        {normalizeRenderedMarkdown(content)}
                                    </ReactMarkdown>
                                )}
                            </article>
                        </AnnotationProvider>
                    </div>
                </ExecutionContextProvider>
            </DocOutputsProvider>
        </DocContextProvider>
    );
});

function readDefinitionOutputForPane(
    tabId: string,
    definitionId: string,
    documentOutputs: Map<string, PluginOutput>,
    cache: Map<string, CachedDocOutputState>,
): PluginOutput | undefined {
    const executionStore = usePluginExecutionStore.getState();
    const editorStore = useEditorStore.getState();
    const exec = getPaneExecutionEntry(
        executionStore,
        tabId,
        editorStore.activeTabId,
        definitionId,
    );
    const partialOutput = exec?.status === "running"
        ? exec.progress?.partialOutput
        : undefined;
    const baseOutput =
        exec?.output ??
        documentOutputs.get(definitionId) ??
        (partialOutput as PluginOutput | undefined);

    if (!baseOutput) {
        cache.delete(definitionId);
        return undefined;
    }

    const cached = cache.get(definitionId);
    if (
        cached &&
        cached.baseOutput === baseOutput &&
        cached.partialOutput === partialOutput
    ) {
        return cached.value;
    }

    const value = partialOutput && partialOutput !== baseOutput
        ? ({ ...baseOutput, ...partialOutput } as PluginOutput)
        : baseOutput;
    cache.set(definitionId, { baseOutput, partialOutput, value });
    return value;
}

function areDefinitionExecutionStatesEqual(
    a: DefinitionExecutionState,
    b: DefinitionExecutionState,
): boolean {
    return a.isPinned === b.isPinned &&
        a.isRunning === b.isRunning &&
        a.status === b.status &&
        a.duration === b.duration &&
        a.computedAt === b.computedAt &&
        a.errorMessage === b.errorMessage &&
        a.progress?.message === b.progress?.message &&
        a.progress?.percent === b.progress?.percent;
}

function cacheDefinitionExecutionState(
    cache: Map<string, DefinitionExecutionState>,
    definitionId: string,
    next: DefinitionExecutionState,
): DefinitionExecutionState {
    const prev = cache.get(definitionId);
    if (prev && areDefinitionExecutionStatesEqual(prev, next)) return prev;
    if (areDefinitionExecutionStatesEqual(EMPTY_DEFINITION_EXECUTION_STATE, next)) {
        cache.delete(definitionId);
        return EMPTY_DEFINITION_EXECUTION_STATE;
    }
    cache.set(definitionId, next);
    return next;
}

function readDefinitionExecutionStateForPane(
    tabId: string,
    definitionId: string,
    cache: Map<string, DefinitionExecutionState>,
): DefinitionExecutionState {
    const executionStore = usePluginExecutionStore.getState();
    const editorStore = useEditorStore.getState();
    const exec = getPaneExecutionEntry(
        executionStore,
        tabId,
        editorStore.activeTabId,
        definitionId,
    );
    const tab = editorStore.tabs.find((item) => item.id === tabId);
    const isPinned = !!findWorkspacePinnedUnit(
        editorStore.pinnedUnits,
        tab?.filePath,
        definitionId,
    );
    if (!exec && !isPinned) return cacheDefinitionExecutionState(
        cache,
        definitionId,
        EMPTY_DEFINITION_EXECUTION_STATE,
    );

    const status = exec?.status;
    const duration = exec?.duration ?? undefined;
    const computedAt =
        exec?.startedAt != null && exec.duration != null
            ? exec.startedAt + exec.duration
            : undefined;
    const progress = exec?.status === "running" && exec.progress
        ? {
            message: exec.progress.message,
            ...(exec.progress.percent != null ? { percent: exec.progress.percent } : {}),
        }
        : undefined;
    const errorMessage = exec?.result && !exec.result.success && exec.result.message
        ? exec.result.message
        : null;

    return cacheDefinitionExecutionState(cache, definitionId, {
        isPinned,
        isRunning: status === "running",
        ...(status ? { status } : {}),
        ...(duration != null ? { duration } : {}),
        ...(computedAt != null ? { computedAt } : {}),
        ...(progress ? { progress } : {}),
        errorMessage,
    });
}

function subscribePaneExecutionEntry<T>(
    tabId: string,
    id: string,
    selector: ExecutionMapSelector<T>,
    readEntry: (id: string) => T | undefined,
    onStoreChange: () => void,
): () => void {
    let previous = readEntry(id);
    const notifyIfChanged = () => {
        const next = readEntry(id);
        if (next === previous) return;
        previous = next;
        onStoreChange();
    };
    const unsubscribeExecution = usePluginExecutionStore.subscribe((state, prevState) => {
        const activeTabId = useEditorStore.getState().activeTabId;
        const nextEntry = selector(state, tabId, activeTabId).get(id);
        const prevEntry = selector(prevState, tabId, activeTabId).get(id);
        if (nextEntry !== prevEntry) notifyIfChanged();
    });
    const unsubscribeEditor = useEditorStore.subscribe((state, prevState) => {
        if (state.activeTabId === prevState.activeTabId) return;
        notifyIfChanged();
    });
    return () => {
        unsubscribeExecution();
        unsubscribeEditor();
    };
}

/* ── Main Preview Panel ── */

export const PreviewPanel = memo(function PreviewPanel() {
    return (
        <PaneRenderProvider>
            <PreviewPanelInner />
        </PaneRenderProvider>
    );
});

const PreviewPanelInner = memo(function PreviewPanelInner() {
    const pluginRegistryVersion = useSyncExternalStore(
        subscribePluginRegistry,
        getPluginRegistrySnapshot,
        getPluginRegistrySnapshot,
    );

    const paneCtx = usePaneRenderContext();
    const { parsedDocument, tab: activeTab, paneId, previewWidth } = paneCtx;
    const activeTabId = useEditorStore((s) => s.activeTabId);
    const setPanePreviewWidth = useEditorStore((s) => s.setPanePreviewWidth);
    const setPreviewWidth = useCallback(
        (width: PreviewWidth) => setPanePreviewWidth(paneId, width),
        [setPanePreviewWidth, paneId],
    );
    const isLoading = !!activeTab?.isLoading;
    const fileKind = getFileKind(activeTab?.filePath);
    const isMarkdownView = fileKind === "markdown";
    const previewScrollRef = useRef<HTMLDivElement | null>(null);
    const previewContentRef = useRef<HTMLDivElement | null>(null);
    const previewResizeRef = useRef<{
        side: "left" | "right";
        startX: number;
        startWidth: number;
        availableWidth: number;
    } | null>(null);
    const [previewAvailableWidth, setPreviewAvailableWidth] = useState<number | null>(null);
    const [isPreviewWidthDragging, setIsPreviewWidthDragging] = useState(false);
    const { control: previewWidthControl, customWidth: customPreviewWidth } =
        useMemo(() => splitPreviewWidthValue(previewWidth), [previewWidth]);

    /* ── Store selectors for RenderEnvironment assembly ── */

    const runAction = usePluginExecutionStore((s) => s.runAction);
    const callApi = usePluginExecutionStore((s) => s.callApi);
    const runPrimary = usePluginExecutionStore((s) => s.runPrimary);
    const runForAction = usePluginExecutionStore((s) => s.runForAction);
    const cancelExecution = usePluginExecutionStore((s) => s.cancelExecution);
    const runContentAction = usePluginExecutionStore((s) => s.runContentAction);
    const runImportAction = usePluginExecutionStore((s) => s.runImportAction);
    const runImportedSourceAction = usePluginExecutionStore((s) => s.runImportedSourceAction);
    const runUseAction = usePluginExecutionStore((s) => s.runUseAction);
    const hydrateOutput = usePluginExecutionStore((s) => s.hydrateOutput);
    const ensureSession = usePluginExecutionStore((s) => s._ensureSession);

    const setRightSidebarDefinitionId = useEditorStore((s) => s.setRightSidebarDefinitionId);
    const rightSidebarOpen = useEditorStore((s) => s.rightSidebarOpen);
    const toggleRightSidebar = useEditorStore((s) => s.toggleRightSidebar);
    const addPinnedUnit = useEditorStore((s) => s.addPinnedUnit);
    const removePinnedUnit = useEditorStore((s) => s.removePinnedUnit);
    const openFileFromPath = useEditorStore((s) => s.openFileFromPath);

    useEffect(() => {
        if (isLoading || paneCtx.tabId !== activeTabId || paneCtx.sessionId) return;
        if (!parsedDocument) return;

        const hasExecutableSyntax =
            parsedDocument.definitions.size > 0 ||
            (parsedDocument.forDefinitions?.size ?? 0) > 0 ||
            (parsedDocument.uses?.length ?? 0) > 0 ||
            (parsedDocument.imports?.length ?? 0) > 0 ||
            (parsedDocument.contentDefinitions?.size ?? 0) > 0;

        if (!hasExecutableSyntax) return;

        ensureSession().catch((err) => {
            console.warn(
                "[magic-md] Preview auto-session failed:",
                err?.message ?? err,
            );
        });
    }, [
        activeTabId,
        ensureSession,
        isLoading,
        paneCtx.sessionId,
        paneCtx.tabId,
        parsedDocument,
    ]);

    /* ── Derived data for RenderEnvironment ── */

    const {
        definitions,
        documentDefinitionOutputs,
    } = paneCtx;
    const latestActiveTabRef = useRef(activeTab);
    const latestParsedDocumentRef = useRef(parsedDocument);
    latestActiveTabRef.current = activeTab;
    latestParsedDocumentRef.current = parsedDocument;

    const localExecutionUnitIds = useMemo(() => {
        const ids = new Set<string>();
        if (!parsedDocument) return ids;

        for (const id of parsedDocument.definitions.keys()) ids.add(id);
        for (const id of parsedDocument.contentDefinitions?.keys() ?? []) ids.add(id);
        for (const id of parsedDocument.forDefinitions?.keys() ?? []) ids.add(id);
        for (const use of parsedDocument.uses ?? []) ids.add(use.id);

        return ids;
    }, [parsedDocument]);
    const stableLocalExecutionUnitIds = useStableSemanticValue(localExecutionUnitIds);
    const importBindings = useStableSemanticValue(
        parsedDocument?.imports ?? EMPTY_IMPORT_BINDINGS,
    );
    const documentBlocks = useStableDocumentBlocks(parsedDocument?.blocks ?? null);

    // Build forBlocksMap from parsed document blocks
    const rawForBlocksMap = useMemo(() => {
        if (!parsedDocument) return undefined;
        const map = new Map<string, ForBlock>();
        for (const block of parsedDocument.blocks) {
            if (block.type === "for-block") {
                const forDefinition = parsedDocument.forDefinitions?.get(block.id);
                map.set(block.id, {
                    ...block,
                    template: forDefinition?.template ?? block.template,
                    params: forDefinition?.params ?? block.params,
                    externalRef: forDefinition?.externalRef ?? block.externalRef,
                });
            }
        }
        for (const [id, block] of paneCtx.engineForBlocks) {
            if (!map.has(id)) {
                map.set(id, block);
            }
        }
        return map.size > 0 ? map : undefined;
    }, [parsedDocument, paneCtx.engineForBlocks]);
    const forBlocksMap = useStableSemanticValue(rawForBlocksMap);

    /* ── Callbacks for RenderEnvironment ── */

    const runScopedUnit = useCallback(
        (unitId: string) => {
            const parentContentId = findParentContentBlockId(
                unitId,
                paneCtx.contentDefinitions,
            );
            const parentBody = parentContentId
                ? paneCtx.contentDefinitions.get(parentContentId)?.body
                : undefined;
            const escaped = unitId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            if (parentBody && new RegExp(`::use\\[${escaped}\\]`).test(parentBody)) {
                runUseAction(unitId).catch(() => { });
                return;
            }
            if (parentBody && new RegExp(`::content\\[${escaped}\\]`).test(parentBody)) {
                runContentAction(unitId).catch(() => { });
                return;
            }
            if (parentBody && new RegExp(`::for\\[${escaped}\\]`).test(parentBody)) {
                runForAction(unitId).catch(() => { });
                return;
            }
            if (parentContentId) {
                runContentAction(parentContentId).catch(() => { });
            }
        },
        [paneCtx.contentDefinitions, runContentAction, runForAction, runUseAction],
    );

    const readImportedSourceFile = useCallback(async (filePath: string) => {
        const openTab = useEditorStore
            .getState()
            .tabs.find((tab) => tab.filePath === filePath);
        if (openTab) return openTab.content;
        return (await getPlatformService().engine.workspaceReadFile({ path: filePath })).content;
    }, []);

    const runImportedSourceTarget = useCallback(
        async (
            target: Extract<RenderRunTarget, { type: "imported-source" }>,
            actionName?: string,
            actionParams?: Record<string, unknown>,
        ) => {
            const currentTab = latestActiveTabRef.current;
            const currentParsedDocument = latestParsedDocumentRef.current;
            if (!currentTab?.filePath || !currentParsedDocument) {
                await runImportAction(target.namespace);
                return { success: false, message: "Cannot resolve imported source document" };
            }

            const resolved = await resolveImportedUnitSource({
                unitId: target.definitionId,
                parsedDocument: currentParsedDocument,
                documentPath: currentTab.filePath,
                readFile: readImportedSourceFile,
            });
            if (!resolved) {
                await runImportAction(target.namespace);
                return { success: false, message: "Cannot resolve imported source unit" };
            }

            return runImportedSourceAction({
                renderedUnitId: target.definitionId,
                importNamespace: resolved.topLevelNamespace,
                sourceDocumentPath: resolved.sourceDocumentPath,
                sourceUnitId: resolved.sourceUnitId,
                unitKind: target.kind,
                pluginName: definitions.get(target.definitionId)?.pluginName,
                actionName,
                actionParams,
            });
        },
        [
            definitions,
            readImportedSourceFile,
            runImportAction,
            runImportedSourceAction,
        ],
    );

    const onAction = useCallback(
        (definitionId: string, actionName: string, actionParams?: Record<string, unknown>) => {
            const target = resolveRenderRunTarget(
                definitionId,
                definitions,
                importBindings,
                stableLocalExecutionUnitIds,
            );
            if (target.type === "imported-source") {
                return runImportedSourceTarget(target, actionName, actionParams);
            }
            const def = definitions.get(definitionId);
            if (def) {
                return runAction(def, actionName, definitions, { actionParams });
            }
            if (target.type === "import") {
                if (actionName === "compute") return runImportAction(target.namespace);
                return undefined;
            }
            if (actionName === "compute") {
                return runScopedUnit(definitionId);
            }
            return undefined;
        },
        [definitions, importBindings, runAction, runImportAction, runImportedSourceTarget, runScopedUnit, stableLocalExecutionUnitIds],
    );

    const onApi = useCallback<NonNullable<DocContextValue["onApi"]>>(
        <TData = unknown,>(definitionId: string, apiName: string, params?: Record<string, unknown>) => {
            const target = resolveRenderRunTarget(
                definitionId,
                definitions,
                importBindings,
                stableLocalExecutionUnitIds,
            );
            if (target.type !== "definition") {
                return {
                    success: false,
                    message: "Runtime APIs are only available for local definitions",
                };
            }
            const def = definitions.get(target.definitionId);
            if (!def) {
                return { success: false, message: `Definition not found: ${definitionId}` };
            }
            return callApi<TData>(def, apiName, definitions, params);
        },
        [callApi, definitions, importBindings, stableLocalExecutionUnitIds],
    );

    const onRun = useCallback(
        (definitionId: string) => {
            const target = resolveRenderRunTarget(
                definitionId,
                definitions,
                importBindings,
                stableLocalExecutionUnitIds,
            );
            if (target.type === "definition") {
                const def = definitions.get(target.definitionId);
                if (!def) return;
                runPrimary(def, definitions).catch(() => { });
                return;
            }
            if (target.type === "imported-source") {
                runImportedSourceTarget(target).catch(() => { });
                return;
            }
            if (target.type === "import") {
                runImportAction(target.namespace).catch(() => { });
                return;
            }
            runScopedUnit(target.unitId);
        },
        [definitions, importBindings, runImportAction, runImportedSourceTarget, runPrimary, runScopedUnit, stableLocalExecutionUnitIds],
    );

    const onPin = useCallback(
        (definitionId: string) => {
            const documentPath = activeTab?.filePath;
            const definition = definitions.get(definitionId);
            if (!documentPath || !definition) return;
            const existing = findWorkspacePinnedUnit(
                useEditorStore.getState().pinnedUnits,
                documentPath,
                definitionId,
            );
            if (existing) {
                removePinnedUnit(existing.id);
                return;
            }
            void (async () => {
                const sessionId = await ensureSession(activeTab.id);
                addPinnedUnit(
                    createWorkspacePinnedUnit(documentPath, definition),
                    { sessionId },
                );
            })();
        },
        [activeTab, addPinnedUnit, definitions, ensureSession, removePinnedUnit],
    );

    const onDefinitionSelect = useCallback(
        (definitionId: string) => {
            setRightSidebarDefinitionId(definitionId);
            if (!rightSidebarOpen) {
                toggleRightSidebar();
            }
        },
        [setRightSidebarDefinitionId, rightSidebarOpen, toggleRightSidebar],
    );

    const onDefinitionConfigChange = useCallback(
        (definitionId: string, nextConfig: Record<string, unknown>, options?: RendererConfigChangeOptions) => {
            const currentTab = latestActiveTabRef.current;
            if (!currentTab) return false;
            const target = resolveRenderRunTarget(
                definitionId,
                definitions,
                importBindings,
                stableLocalExecutionUnitIds,
            );
            if (target.type !== "definition") return false;
            const updated = options?.preserveOutput
                ? updateDefinitionConfig(currentTab.content, target.definitionId, nextConfig)
                : updateDefinitionConfigAndDropOutput(
                    currentTab.content,
                    target.definitionId,
                    nextConfig,
                );
            if (updated === currentTab.content) return false;
            useEditorStore.getState().setContentImmediate(updated, currentTab.id);
            return true;
        },
        [
            definitions,
            importBindings,
            stableLocalExecutionUnitIds,
        ],
    );

    const onDebug = useCallback(
        (definitionId: string) => {
            if (paneCtx.sessionId) {
                useLogStore.getState().openDebugPanel({
                    sessionId: paneCtx.sessionId,
                    targetId: definitionId,
                });
            }
        },
        [paneCtx.sessionId],
    );

    const callSidecar = useCallback(
        async (sidecarId: string, method: string, params?: Record<string, unknown>) => {
            return getPlatformService().engine.sidecarCall(sidecarId, method, params);
        },
        [],
    );

    const onEngineNotification = useCallback(
        <T = unknown>(method: string, handler: (params: T) => void): (() => void) => {
            return getNotificationBus().subscribe<T>(method, handler);
        },
        [],
    );

    const pickerInitialPath = pickerInitialPathFromDocument(paneCtx.tab?.filePath);

    const selectFile = useCallback(
        async (options?: { accept?: string[]; title?: string }) => {
            return showFilePickerDialog({
                accept: options?.accept,
                title: options?.title,
                initialPath: pickerInitialPath,
            });
        },
        [pickerInitialPath],
    );

    const selectFolder = useCallback(
        async (options?: { title?: string }) => {
            return showFolderPickerDialog({
                title: options?.title,
                initialPath: pickerInitialPath,
            });
        },
        [pickerInitialPath],
    );

    const openExternal = useCallback(async (url: string) => {
        await getPlatformService().openExternal?.(url);
    }, []);

    const getAssetUrl = useCallback((filePath: string) => {
        return getPlatformService().getAssetUrl?.(filePath);
    }, []);

    const openFile = useCallback(async (filePath: string, target?: MarkdownLinkOpenTarget) => {
        await openFileFromPath(filePath);
        const fragment = target?.fragment;
        if (!fragment) return;

        const state = useEditorStore.getState();
        const openedTab = state.tabs.find((tab) => tab.filePath === filePath);
        const line =
            fragment.kind === "line"
                ? fragment.line
                : findMarkdownHeadingLine(openedTab?.content, fragment.heading);
        if (!line) return;

        state.setPaneCursorPosition(paneId, line, 1);
        state.setCursorPosition(line, 1);
        window.dispatchEvent(
            new CustomEvent("magic-md:goto-line", {
                detail: { line, flash: true },
            }),
        );
    }, [openFileFromPath, paneId]);

    const getTabs = useCallback(
        () =>
            useEditorStore.getState().tabs.map((tab) => ({
                id: tab.id,
                title: tab.title,
                filePath: tab.filePath,
            })),
        [],
    );

    const onRunFor = useCallback(
        (forId: string) => { runForAction(forId).catch(() => { }); },
        [runForAction],
    );

    const onCancelFor = useCallback(
        (forId: string) => { cancelExecution(forId); },
        [cancelExecution],
    );

    const onRunContent = useCallback(
        (contentId: string) => { runContentAction(contentId).catch(() => { }); },
        [runContentAction],
    );

    const onRunUse = useCallback(
        (useId: string) => { runUseAction(useId).catch(() => { }); },
        [runUseAction],
    );

    const onRunImport = useCallback(
        (namespace: string) => { runImportAction(namespace).catch(() => { }); },
        [runImportAction],
    );

    const onLoadOutput = useCallback(
        (definitionId: string) => hydrateOutput(definitionId),
        [hydrateOutput],
    );
    const definitionExecutionStateCacheRef =
        useRef(new Map<string, DefinitionExecutionState>());
    const getDefinitionExecutionState = useCallback(
        (definitionId: string) =>
            readDefinitionExecutionStateForPane(
                paneCtx.tabId,
                definitionId,
                definitionExecutionStateCacheRef.current,
            ),
        [paneCtx.tabId],
    );
    const subscribeDefinitionExecution = useCallback(
        (definitionId: string, onStoreChange: () => void) => {
            const tabId = paneCtx.tabId;
            let previous = getDefinitionExecutionState(definitionId);
            const notifyIfChanged = () => {
                const next = getDefinitionExecutionState(definitionId);
                if (next === previous) return;
                previous = next;
                onStoreChange();
            };
            const unsubscribeExecution = usePluginExecutionStore.subscribe((state, prevState) => {
                const activeTabId = useEditorStore.getState().activeTabId;
                const nextExec = getPaneExecutionEntry(state, tabId, activeTabId, definitionId);
                const prevExec = getPaneExecutionEntry(prevState, tabId, activeTabId, definitionId);
                if (nextExec !== prevExec) notifyIfChanged();
            });
            const unsubscribeEditor = useEditorStore.subscribe((state, prevState) => {
                if (
                    state.activeTabId === prevState.activeTabId &&
                    state.pinnedUnits === prevState.pinnedUnits &&
                    state.tabs === prevState.tabs
                ) {
                    return;
                }
                notifyIfChanged();
            });
            return () => {
                unsubscribeExecution();
                unsubscribeEditor();
            };
        },
        [getDefinitionExecutionState, paneCtx.tabId],
    );

    const definitionOutputStateCacheRef =
        useRef(new Map<string, CachedDocOutputState>());
    useEffect(() => {
        const validDefinitionIds = new Set(definitions.keys());
        prunePreviewDefinitionCaches(
            validDefinitionIds,
            definitionExecutionStateCacheRef.current,
            definitionOutputStateCacheRef.current,
        );
    }, [definitions, paneCtx.tabId]);
    const getDefinitionOutput = useCallback(
        (definitionId: string) =>
            readDefinitionOutputForPane(
                paneCtx.tabId,
                definitionId,
                documentDefinitionOutputs,
                definitionOutputStateCacheRef.current,
            ),
        [documentDefinitionOutputs, paneCtx.tabId],
    );
    const subscribeDefinitionOutput = useCallback(
        (definitionId: string, onStoreChange: () => void) => {
            const tabId = paneCtx.tabId;
            let previous = getDefinitionOutput(definitionId);
            const notifyIfChanged = () => {
                const next = getDefinitionOutput(definitionId);
                if (next === previous) return;
                previous = next;
                onStoreChange();
            };
            const unsubscribeExecution = usePluginExecutionStore.subscribe((state, prevState) => {
                const activeTabId = useEditorStore.getState().activeTabId;
                const nextExec = getPaneExecutionEntry(state, tabId, activeTabId, definitionId);
                const prevExec = getPaneExecutionEntry(prevState, tabId, activeTabId, definitionId);
                if (
                    nextExec?.output !== prevExec?.output ||
                    nextExec?.status !== prevExec?.status ||
                    nextExec?.progress?.partialOutput !== prevExec?.progress?.partialOutput
                ) {
                    notifyIfChanged();
                }
            });
            const unsubscribeEditor = useEditorStore.subscribe((state, prevState) => {
                if (state.activeTabId === prevState.activeTabId) return;
                notifyIfChanged();
            });
            return () => {
                unsubscribeExecution();
                unsubscribeEditor();
            };
        },
        [getDefinitionOutput, paneCtx.tabId],
    );
    const getForExecution = useCallback(
        (forId: string) =>
            getPaneExecutionMapEntry(getPaneForExecutions, paneCtx.tabId, forId),
        [paneCtx.tabId],
    );
    const subscribeForExecution = useCallback(
        (forId: string, onStoreChange: () => void) =>
            subscribePaneExecutionEntry(
                paneCtx.tabId,
                forId,
                getPaneForExecutions,
                getForExecution,
                onStoreChange,
            ),
        [getForExecution, paneCtx.tabId],
    );
    const getContentExecution = useCallback(
        (contentId: string) =>
            getPaneExecutionMapEntry(getPaneContentExecutions, paneCtx.tabId, contentId),
        [paneCtx.tabId],
    );
    const subscribeContentExecution = useCallback(
        (contentId: string, onStoreChange: () => void) =>
            subscribePaneExecutionEntry(
                paneCtx.tabId,
                contentId,
                getPaneContentExecutions,
                getContentExecution,
                onStoreChange,
            ),
        [getContentExecution, paneCtx.tabId],
    );
    const getImportExecution = useCallback(
        (namespace: string) =>
            getPaneExecutionMapEntry(getPaneImportExecutions, paneCtx.tabId, namespace),
        [paneCtx.tabId],
    );
    const subscribeImportExecution = useCallback(
        (namespace: string, onStoreChange: () => void) =>
            subscribePaneExecutionEntry(
                paneCtx.tabId,
                namespace,
                getPaneImportExecutions,
                getImportExecution,
                onStoreChange,
            ),
        [getImportExecution, paneCtx.tabId],
    );
    const getUseExecution = useCallback(
        (useId: string) =>
            getPaneExecutionMapEntry(getPaneUseExecutions, paneCtx.tabId, useId),
        [paneCtx.tabId],
    );
    const subscribeUseExecution = useCallback(
        (useId: string, onStoreChange: () => void) =>
            subscribePaneExecutionEntry(
                paneCtx.tabId,
                useId,
                getPaneUseExecutions,
                getUseExecution,
                onStoreChange,
            ),
        [getUseExecution, paneCtx.tabId],
    );
    const getOutputs = useCallback(() => {
        const map = new Map<string, PluginOutput>(documentDefinitionOutputs);
        for (const definitionId of definitions.keys()) {
            const output = getDefinitionOutput(definitionId);
            if (output) map.set(definitionId, output);
        }
        return map;
    }, [definitions, documentDefinitionOutputs, getDefinitionOutput]);
    const outputStore = useMemo<DocOutputsStore>(
        () => ({
            getOutput: getDefinitionOutput,
            subscribeOutput: subscribeDefinitionOutput,
            getOutputs,
        }),
        [getDefinitionOutput, getOutputs, subscribeDefinitionOutput],
    );

    const latestDefinitionsRef = useRef(definitions);
    const latestGetDefinitionOutputRef = useRef(getDefinitionOutput);
    const latestSourceContentRef = useRef(activeTab?.content);
    latestDefinitionsRef.current = definitions;
    latestGetDefinitionOutputRef.current = getDefinitionOutput;
    latestSourceContentRef.current = activeTab?.content;

    const getDefinitions = useCallback(
        () =>
            Array.from(latestDefinitionsRef.current.values()).map((d) => ({
                id: d.id,
                pluginName: d.pluginName,
                hasOutput: d.output != null || latestGetDefinitionOutputRef.current(d.id) != null,
            })),
        [],
    );
    const getSourceContent = useCallback(
        () => latestSourceContentRef.current,
        [],
    );

    /* ── Assemble context values ── */

    // DocContext: core data + callbacks
    const docValue = useMemo<DocContextValue>(
        () => {
            void pluginRegistryVersion;
            return {
                definitions,
                registry: pluginUIRegistry,
                executable: true,
                documentPath: activeTab?.filePath ?? null,
                getSourceContent,
                documentBundlePath: activeTab?.filePath ? getDocumentBundleDir(activeTab.filePath) : null,
                contentDefinitions: paneCtx.contentDefinitions,
                forBlocksMap,
                importBindings,
                useBindings: paneCtx.uses,
                importDocuments: paneCtx.importDocuments,
                localExecutionUnitIds: stableLocalExecutionUnitIds,
                onAction,
                onApi,
                onDefinitionConfigChange,
                onRun,
                onPin,
                onDefinitionSelect,
                onDebug,
                onRunFor,
                onCancelFor,
                onRunContent,
                onRunImport,
                onRunUse,
                onLoadOutput,
                callSidecar,
                onEngineNotification,
                selectFile,
                selectFolder,
                openFile,
                openExternal,
                getAssetUrl,
                getDefinitions,
                getTabs,
                ContentBlockWrapper,
            };
        },
        [
            pluginRegistryVersion,
            definitions, forBlocksMap,
            activeTab?.filePath, getSourceContent,
            importBindings, paneCtx.uses, stableLocalExecutionUnitIds,
            paneCtx.contentDefinitions, paneCtx.importDocuments,
            onAction, onApi, onDefinitionConfigChange, onRun, onPin, onDefinitionSelect, onDebug,
            onRunFor, onCancelFor, onRunContent, onRunImport, onRunUse,
            onLoadOutput,
            callSidecar, onEngineNotification, selectFile, selectFolder, openFile, openExternal, getAssetUrl,
            getDefinitions, getTabs,
        ],
    );

    // ExecutionContext: non-definition maps plus per-definition subscriptions.
    const execValue = useMemo<ExecutionContextValue>(
        () => ({
            getDefinitionExecutionState,
            subscribeDefinitionExecution,
            getForExecution,
            subscribeForExecution,
            getContentExecution,
            subscribeContentExecution,
            getImportExecution,
            subscribeImportExecution,
            getUseExecution,
            subscribeUseExecution,
            sessionConnected: !!paneCtx.sessionId,
        }),
        [
            getDefinitionExecutionState,
            subscribeDefinitionExecution,
            getForExecution,
            subscribeForExecution,
            getContentExecution,
            subscribeContentExecution,
            getImportExecution,
            subscribeImportExecution,
            getUseExecution,
            subscribeUseExecution,
            paneCtx.sessionId,
        ],
    );

    /* ── LazyBlock wrapper for top-level blocks ── */

    const lazyBlockWrapper = useCallback(
        ({ children, block, blockKey }: {
            children: React.ReactNode;
            block: DocumentBlock | DocumentBlock[];
            index: number;
            blockKey: string;
        }) => {
            const estHeight = Array.isArray(block)
                ? 40
                : block.type === "markdown"
                    ? Math.max(40, Math.ceil(block.content.length / 50) * 22)
                    : 80;
            return (
                <LazyBlock key={`lazy-${blockKey}`} estimatedHeight={estHeight}>
                    {children}
                </LazyBlock>
            );
        },
        [],
    );

    const resolvedPreviewViewportWidth = resolvePreviewViewportWidth(
        previewWidthControl,
        customPreviewWidth,
        previewAvailableWidth,
    );
    const previewContentStyle = useMemo(
        () => ({
            width:
                resolvedPreviewViewportWidth === "fill"
                    ? "100%"
                    : `${resolvedPreviewViewportWidth}px`,
            maxWidth: "100%",
        }) satisfies CSSProperties,
        [resolvedPreviewViewportWidth],
    );

    const resetPreviewWidth = useCallback(() => {
        setPreviewWidth("fill");
    }, [setPreviewWidth]);

    const startPreviewWidthResize = useCallback(
        (side: "left" | "right", event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            const rect = previewContentRef.current?.getBoundingClientRect();
            const currentWidth =
                rect?.width ??
                (resolvedPreviewViewportWidth === "fill"
                    ? previewAvailableWidth
                    : resolvedPreviewViewportWidth);
            const availableWidth =
                previewAvailableWidth ?? readPreviewAvailableWidth(previewScrollRef.current);
            if (!currentWidth || !availableWidth) return;
            previewResizeRef.current = {
                side,
                startX: event.clientX,
                startWidth: currentWidth,
                availableWidth,
            };
            setIsPreviewWidthDragging(true);
        },
        [previewAvailableWidth, resolvedPreviewViewportWidth],
    );

    useEffect(() => {
        if (!isMarkdownView) {
            setPreviewAvailableWidth(null);
            return;
        }

        const element = previewScrollRef.current;
        if (!element || typeof window === "undefined") return;

        const updateWidth = () => {
            const next = readPreviewAvailableWidth(element);
            setPreviewAvailableWidth((current) => (current === next ? current : next));
        };

        updateWidth();
        window.addEventListener("resize", updateWidth);

        if (typeof ResizeObserver === "undefined") {
            return () => window.removeEventListener("resize", updateWidth);
        }

        const observer = new ResizeObserver(updateWidth);
        observer.observe(element);
        return () => {
            window.removeEventListener("resize", updateWidth);
            observer.disconnect();
        };
    }, [activeTab?.id, isMarkdownView]);

    useEffect(() => {
        if (!isPreviewWidthDragging || typeof window === "undefined") return;

        const onMove = (event: globalThis.MouseEvent) => {
            const state = previewResizeRef.current;
            if (!state) return;
            const delta = event.clientX - state.startX;
            const nextWidth =
                state.side === "right"
                    ? state.startWidth + delta
                    : state.startWidth - delta;
            setPreviewWidth(
                createCustomPreviewWidth(
                    clampPreviewViewportWidth(nextWidth, state.availableWidth),
                ),
            );
        };

        const onUp = () => {
            previewResizeRef.current = null;
            setIsPreviewWidthDragging(false);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";

        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [isPreviewWidthDragging, previewAvailableWidth, setPreviewWidth]);

    if (!activeTab) {
        return (
            <div className="flex items-center justify-center h-full text-muted-foreground">
                No document open
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full min-w-0 bg-preview-surface">
            {/* Preview content */}
            <div ref={previewScrollRef} className="flex-1 min-h-0 overflow-auto px-4 py-4">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center h-full gap-2">
                        <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                        <span className="text-xs text-muted-foreground">Loading…</span>
                    </div>
                ) : isMarkdownView ? (
                    <MarkdownPreviewDocument
                        docValue={docValue}
                        outputStore={outputStore}
                        execValue={execValue}
                        previewContentRef={previewContentRef}
                        previewContentStyle={previewContentStyle}
                        previewWidthControl={previewWidthControl}
                        isPreviewWidthDragging={isPreviewWidthDragging}
                        startPreviewWidthResize={startPreviewWidthResize}
                        resetPreviewWidth={resetPreviewWidth}
                        documentPath={activeTab.filePath}
                        content={activeTab.content}
                        blocks={documentBlocks}
                        importDocuments={paneCtx.importDocuments}
                        blockWrapper={lazyBlockWrapper}
                        openFile={openFile}
                        openExternal={openExternal}
                    />
                ) : fileKind === "json" ? (
                    <JsonTreePreview content={activeTab.content} />
                ) : fileKind === "html" ? (
                    <HtmlIframePreview content={activeTab.content} />
                ) : fileKind === "svg" ? (
                    <ImagePreview kind="svg" svgSource={activeTab.content} />
                ) : fileKind === "image" ? (
                    <ImagePreview kind="image" filePath={activeTab.filePath} />
                ) : fileKind === "pdf" ? (
                    <PdfPreview filePath={activeTab.filePath} />
                ) : fileKind === "audio" ? (
                    <MediaPreview kind="audio" filePath={activeTab.filePath} />
                ) : fileKind === "video" ? (
                    <MediaPreview kind="video" filePath={activeTab.filePath} />
                ) : fileKind === "csv" ? (
                    <CsvPreview
                        content={activeTab.content}
                        delimiter={activeTab.filePath?.toLowerCase().endsWith(".tsv") ? "\t" : ","}
                    />
                ) : (
                    <CodeTextPreview content={activeTab.content} />
                )}
            </div>
        </div>
    );
});
